<template>
    <div>
        <div :class="{ 'large-navbar': useLargeNavBar, 'web-map': true }">
            <b-link
                v-if="getWebThemeParameters.tma"
                :to="getAccessibleInfo"
                class="sr-only"
                >{{ $t('accessibility.AccessibilityView') }}</b-link
            >
            <!-- Show this button only for a web 'meldingenkaart' that contains line/polygon geometry
                and hide it when it is clicked  -->
            <b-button
                v-if="
                    isWebFeedbackWithLineOrPolygonGeometry && !showDrawToolbar
                "
                variant="primary"
                class="float-right create-feedback"
                :class="{ 'has-search': showSearch }"
                @click="$_createFeedback"
            >
                {{ buttonText }}
            </b-button>
            <nav-bar-large v-if="useLargeNavBar" />
            <nav-bar v-else />
            <div class="map-container" ref="mapContainer">
                <div ref="nedglobeMap" id="nedglobe.map" class="map"></div>
                <web-panels />
                <fullscreen-button v-if="showFullScreen"></fullscreen-button>
                <div
                    class="zoom-container"
                    :class="{ 'has-fullscreen': showFullScreen }"
                >
                    <zoom-buttons v-if="showZoom"></zoom-buttons>
                    <geolocation-button
                        v-if="showGpsTracker"
                        class="mb-1"
                    ></geolocation-button>
                    <div id="rotationButton" class="rotation-container"></div>
                </div>
                <div class="baselayer-container" :style="$_getStyle">
                    <baselayer-toggler></baselayer-toggler>
                    <div class="scale-container">
                        <div id="scaleLine" class="scaleline"></div>
                        <scale-selector
                            :minScale="minScale"
                            :maxScale="maxScale"
                        ></scale-selector>
                    </div>
                </div>
                <span class="footer-link-container">
                    <conditions />
                    <terms />
                </span>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapActions, mapGetters, mapState } from 'vuex';
    import OlClusterSource from 'ol/source/Cluster';
    import { debounce, throttle } from 'lodash';
    import Select from 'ol/interaction/Select';
    import { windowMessageReceiver } from '../../../services/utilities/utilities';

    import {
        MAP_INTERACTION_TIMEOUTS,
        INTERFACE_PANEL_TRIGGERS,
        MESSAGE_TYPE,
        PROJECTION_AMERSFOORT_RD_BOUNDS,
        CONTENT_TYPE_ID
    } from '../../../constants';

    import {
        getResolutionFromScaleDpi,
        getScaleFromResolution
    } from '../../../services/map';

    import WebNavBar from '../panels/navbar/WebNavBar.vue';
    import WebNavBarLarge from '../panels/navbar/WebNavBarLarge.vue';
    import BaselayerToggler from '../buttons/BaselayerToggler.vue';
    import GeoLocationButton from '../buttons/GeoLocationButton.vue';
    import ZoomButtons from '../buttons/ZoomButtons.vue';
    import FullScreenButton from '../buttons/FullScreenButton.vue';
    import Conditions from '../buttons/Conditions.vue';
    import Terms from '../buttons/Terms.vue';
    import ScaleSelector from '../display/ScaleSelector.vue';

    import WebPanels from '../panels/Web';
    import tinykeys from 'tinykeys';

    export default {
        components: {
            'nav-bar': WebNavBar,
            'nav-bar-large': WebNavBarLarge,
            'baselayer-toggler': BaselayerToggler,
            'geolocation-button': GeoLocationButton,
            'fullscreen-button': FullScreenButton,
            'zoom-buttons': ZoomButtons,
            'web-panels': WebPanels,
            'scale-selector': ScaleSelector,
            conditions: Conditions,
            terms: Terms
        },

        data() {
            return {
                askConfirmUnSubscribe: null,
                map: null,
                minScale: null,
                maxScale: null
            };
        },

        created() {
            if (this.$route.query.devInfo == 1) {
                this.$store.commit('SET_DEV', true);
            }
        },

        mounted() {
            this.init();

            // Ctrl+Alt+T will redirect to accessibility mode.
            tinykeys(window, {
                'Control+Alt+KeyT': (event) => {
                    this.$router.push(this.getAccessibleInfo);
                }
            });
        },

        destroyed() {
            this.askConfirmUnSubscribe && this.askConfirmUnSubscribe();
            this.$_unsubscribeToWindowsMessages();
        },

        computed: {
            ...mapGetters([
                'getMainMap',
                'getWebTheme',
                'getOrganizationName',
                'getWebThemeParameters',
                'getWebThemeHasFeedbackType',
                'getDetailInformationPanelHeight',
                'getFeedbacks',
                'getAllOrderedLayers'
            ]),
            ...mapState(['uiElements']),
            informationPanel() {
                return this.uiElements[INTERFACE_PANEL_TRIGGERS.INFORMATION];
            },
            baseLayerPanel: function () {
                return this.uiElements[INTERFACE_PANEL_TRIGGERS.BASELAYER];
            },
            webFeedbackPanel: function () {
                return this.uiElements[INTERFACE_PANEL_TRIGGERS.WEB_FEEDBACK];
            },
            menuComponents: function () {
                return this.uiElements.menuBar.components;
            },
            webTheme() {
                return this.getWebTheme?.data;
            },
            showGpsTracker() {
                return this.getMainMap?.gpsTracker && this.webTheme?.showGPSTracker;
            },
            showFullScreen() {
                return this.webTheme?.showFullScreen;
            },
            showZoom() {
                return this.webTheme?.showZoom;
            },
            showSearch() {
                return this.webTheme?.showSearch;
            },
            showDrawToolbar: function () {
                return this.webFeedbackPanel?.showDrawToolbar;
            },
            useLargeNavBar() {
                const showToolbar =
                    this.menuComponents.logo.visible &&
                    (this.getWebThemeParameters.startedFromPortal ||
                        this.webTheme?.showToolbar);

                const isFeedBackEdit =
                    this.uiElements[INTERFACE_PANEL_TRIGGERS.FEEDBACK_EDIT]
                        ?.visible === true;

                return showToolbar || isFeedBackEdit;
            },
            $_getStyle() {
                const resize = this.baseLayerPanel.resize;
                return resize
                    ? `bottom: ${this.getDetailInformationPanelHeight + 5}px`
                    : null;
            },
            getAccessibleInfo() {
                return {
                    name: 'accessibleInfo',
                    params: {
                        // Get the guid value if available, Id otherwise
                        tma:
                            this.getWebThemeParameters.guid ||
                            this.getWebThemeParameters.tma
                    }
                };
            },
            isWebFeedbackWithLineOrPolygonGeometry() {
                if (this.$store.getters.getWebThemeHasFeedbackType) {
                    return !this.$store.getters.getWebThemeFeedbackHasPointGeoOnly;
                }
                return false;
            },
            // Get the buttontext from the storageLayer if available and not null
            // otherwise return the default buttontext
            buttonText() {
                const feedbacks = this.getFeedbacks;
                if (feedbacks[0]?.data?.buttonText) {
                    return feedbacks[0]?.data?.buttonText;
                } else {
                    return this.$t('createFeedback');
                }
            }
        },
        watch: {
            'informationPanel.loading': function (newVal) {
                this.$store.dispatch('setLoader', { loading: newVal });
            },
            '$route.name': function (newVal) {
                if (newVal == 'ngMap' && this.map) {
                    this.map.updateSize();
                }
            }
        },

        methods: {
            ...mapActions(['startNedGlobe', 'togglePanel']),

            $_createFeedback() {
                this.$store.commit('SHOW_MENU_FEEDBACK', {
                    active: true,
                    contentTypeId: CONTENT_TYPE_ID.FEEDBACK
                });

                this.$store.commit('TOGGLE_MENU_WEB_FEEDBACK', {
                    active: false,
                    showDrawToolbar: true,
                    data: null
                });
            },
            async init() {
                let webThemeParameters = {
                    tma: this.$route.query.tma,
                    width: this.$route.query.w ?? 0,
                    height: this.$route.query.h ?? 0,
                    center: this.$route.query.c,
                    zoom: this.$route.query.z,
                    startedFromPortal: this.$route.query.p
                };

                let mapConfig = '';

                // Check the webtheme ID and request it if a guid was in the URL
                webThemeParameters = await this.$store.dispatch('checkTmaId', {
                    webThemeParameters
                });

                if (webThemeParameters.tma === 0) {
                    this.$router.push('/error/webtheme_unknown');
                }

                if (
                    !this.$_checkContainerSize(
                        webThemeParameters.width,
                        webThemeParameters.height
                    )
                ) {
                    webThemeParameters.width = 0;
                    webThemeParameters.height = 0;
                }

                if (!this.$_checkZoomLevel(webThemeParameters.zoom)) {
                    // Set zoom as a string so getter function getwebThemeParameters can handle it as a string
                    webThemeParameters.zoom = '7';
                }

                try {
                    mapConfig = await this.startNedGlobe({
                        mapElementId: 'nedglobe.map',
                        webThemeParameters
                    });
                } catch (error) {
                    this.$router.push('/error/webtheme_unknown');
                }

                // Apply min and max scale if we are not in Preview mode.
                if (!window.location.href.includes('previewid')) {
                    this.minScale = this.getWebTheme.data.minScale;
                    this.maxScale = this.getWebTheme.data.maxScale;
                }

                // Check availability of themeLayers
                this.$_checkThemeLayers();

                // onderstaande controle even niet uitvoeren
                // het is "normaal" dat een themakaart geen ondergrondlagen heeft
                // Check availability of baseLayers
                // this.$_checkLayers('base');

                this.$store.subscribe((mutation) => {
                    this.$store.dispatch('updateMainMap', { mutation });
                });
                this.askConfirmUnSubscribe = this.$store.subscribeAction({
                    before: (action, state) => {
                        if (action.type === 'askConfirm') {
                            const { message, messageVars, options } =
                                action.payload;
                            action.payload.msgBox = this.$bvModal.msgBoxConfirm(
                                this.$t(message, messageVars),
                                options
                            );
                        }
                    }
                });
                this.map = mapConfig.map;
                this.$_listenToMap(mapConfig);
                this.$_subscribeToWindowsMessages();
                this.$_setDocumentTitle();
                this.$_setContainerSize();

                // if the center is invalid set the center to the middle of the organization view
                // and update map viewport
                if (this.$_invalidCenter(this.getWebThemeParameters.center)) {
                    let webThemeParameters = this.getWebThemeParameters;
                    webThemeParameters.center = this.$_getRDCenter();

                    // Save new center
                    this.$store.commit(
                        'SET_WEBTHEME_PARAMETERS',
                        webThemeParameters
                    );

                    // update map viewport
                    this.$store.dispatch('updateViewPortMap', {
                        zoom: this.getWebThemeParameters.zoom,
                        center: this.getWebThemeParameters.center
                    });
                }

                this.$_postMessageToDashboard();
            },
            $_postMessageToDashboard() {
                // Create message and send it to Dashboard domain
                const scaleData = {
                    type: MESSAGE_TYPE.GetCurrentScale,
                    scale: Math.round(
                        getScaleFromResolution(
                            this.map,
                            this.map.getView().getResolution()
                        )
                    )
                };
                // Update preview map location and scale if webtheme is part of Dashboard
                if (window.location.href.includes('previewid')) {
                    window.top.postMessage(
                        JSON.stringify(scaleData),
                        windowMessageReceiver(window.location.origin)
                    );
                }
            },
            $_listenToMap(mapConfig) {
                const { dispatch, commit } = this.$store;
                const mapName = mapConfig.name;
                const map = mapConfig.map;
                const longHold = MAP_INTERACTION_TIMEOUTS.longHold;
                const debounceDefaultWaitMs =
                    MAP_INTERACTION_TIMEOUTS.debounceDefaultWaitMs;

                map.once('postrender', () => {
                    const selectCluster = new Select({
                        style: null,
                        layers: (layer) => {
                            return layer.getSource() instanceof OlClusterSource;
                        }
                    });
                    selectCluster.on('select', function (e) {
                        if (e.selected.length > 0) {
                            commit('MAP_SELECT_CLUSTER', {
                                mapName,
                                features: e.selected,
                                coordinate: e.coordinate
                            });
                            selectCluster.getFeatures().clear();
                        }
                    });

                    map.on(
                        'moveend',
                        debounce(() => {
                            dispatch('mapMoveEnd', { mapName });
                        }, debounceDefaultWaitMs)
                    );

                    map.on(
                        'pointerdrag',
                        debounce(() => {
                            dispatch('mapPointerdrag', { mapName });
                        }, debounceDefaultWaitMs)
                    );

                    // On Map click
                    map.on('click', (event) => {
                        // give some time to record cluster click
                        if (this.getWebThemeHasFeedbackType) {
                            setTimeout(() => {
                                dispatch('mapClicked', { mapName, event });
                            }, longHold);
                        } else {
                            dispatch('mapClicked', { mapName, event });
                        }
                    });

                    map.addInteraction(selectCluster);

                    // On Zoom change
                    map.getView().on(
                        'change:resolution',
                        throttle(
                            () => {
                                dispatch('mapResolutionChanged', { mapName });
                            },
                            300,
                            { leading: false, trailing: true }
                        )
                    );
                });
            },
            /*
             * Receive message from Dashboard, used to reset to default Web view within IFrame
             */
            $_subscribeToWindowsMessages() {
                // subscribe to onmessage event of windows
                window.addEventListener(
                    'message',
                    this.$_handleWindowMessages,
                    false
                );
            },
            $_unsubscribeToWindowsMessages() {
                // unsubscribe from onmessage event
                window.removeEventListener(
                    'message',
                    this.$_handleWindowMessages,
                    false
                );
            },
            /**
             * Reacts to posted message from Dashboard when Web application is viewed in the iFrame preview within Dashboard
             * Does either:
             * 1) If type is SetMapLocationDefault, set the coordinates & zoomLevel if possible
             * 2) If type: SetMapLocationOrganizationBbox, center on Organization bounding box
             * 3) If type: SetMapLocationScale, set the scale
             * @param event
             */
            $_handleWindowMessages(event) {
                if (typeof event.data === 'string' && event.data.includes('type')) {
                    const { getters, dispatch } = this.$store;
                    const message = JSON.parse(event.data);
                    if (message.type === MESSAGE_TYPE.SetMapLocationDefault) {
                        dispatch('updateViewPortMap', {
                            zoom: getters.getWebThemeParameters.zoom,
                            center: getters.getWebThemeParameters.center
                        });
                    }
                    if (message.type === MESSAGE_TYPE.UpdateMaplocation) {
                        if (
                            message.coordinates !== undefined &&
                            message.coordinates != null &&
                            message.zoomLevel !== undefined &&
                            message.zoomLevel != null
                        ) {
                            dispatch('updateViewPortMap', {
                                zoom: message.zoomLevel,
                                center: message.coordinates
                            });
                        } else {
                            dispatch('zoomToExtent');
                        }
                    }
                    if (
                        message.type === MESSAGE_TYPE.SetMapLocationOrganizationBbox
                    ) {
                        dispatch('zoomToExtent');
                    }
                    if (message.type === MESSAGE_TYPE.SetMapLocationScale) {
                        dispatch('updateViewPortMap', {
                            resolution: getResolutionFromScaleDpi(message.scale)
                        });
                    }
                }
            },
            $_setContainerSize() {
                const mapContainer = this.$refs.mapContainer;
                const params = this.getWebThemeParameters;
                if (mapContainer && params) {
                    if (
                        params.width !== undefined &&
                        params.width !== null &&
                        params.width !== '0'
                    ) {
                        mapContainer.style.width = `${params.width}px`;
                    }
                    if (
                        params.height !== undefined &&
                        params.height !== null &&
                        params.height !== '0'
                    ) {
                        mapContainer.style.height = `${params.height}px`;
                    }
                }
            },
            $_setDocumentTitle() {
                let suffix = this.webTheme.portalGroup;
                if (!suffix || suffix === '') {
                    suffix = this.getOrganizationName;
                }
                document.title = `${this.webTheme.alias} - ${suffix}`;
            },

            // if zoomlevel is anything other than null it must be 1-20
            $_checkZoomLevel(zoom) {
                if (zoom) {
                    const zoomLevel = parseFloat(zoom.replace(',', '.'));
                    return !isNaN(zoomLevel) && zoomLevel >= 1 && zoomLevel <= 20;
                } else {
                    return true;
                }
            },

            // width and height must be numbers
            // both can be 0
            // or they both need to be between 200 and 2000
            $_checkContainerSize(w, h) {
                let width = 0;
                let height = 0;

                if (w) {
                    width = parseInt(w, 10);
                }
                if (h) {
                    height = parseInt(h, 10);
                }
                if (!isNaN(width) && !isNaN(height)) {
                    if (width === 0 && height === 0) {
                        return true;
                    }

                    if (
                        width >= 200 &&
                        width <= 2000 &&
                        height >= 200 &&
                        height <= 2000
                    ) {
                        return true;
                    }
                }
                return false;
            },
            // return the middle of the organization viewport
            $_getRDCenter() {
                return [
                    (PROJECTION_AMERSFOORT_RD_BOUNDS.minX +
                        PROJECTION_AMERSFOORT_RD_BOUNDS.maxX) *
                        0.5,
                    (PROJECTION_AMERSFOORT_RD_BOUNDS.minY +
                        PROJECTION_AMERSFOORT_RD_BOUNDS.maxY) *
                        0.5
                ];
            },
            /**
             * Check if center is within bounds of Amersfoort RD extent
             * @param center
             */

            $_invalidCenter(center) {
                if (center) {
                    const centerX = center[0];
                    const centerY = center[1];
                    if (
                        centerX < PROJECTION_AMERSFOORT_RD_BOUNDS.minX ||
                        centerX > PROJECTION_AMERSFOORT_RD_BOUNDS.maxX ||
                        centerY < PROJECTION_AMERSFOORT_RD_BOUNDS.minY ||
                        centerY > PROJECTION_AMERSFOORT_RD_BOUNDS.maxY
                    ) {
                        return true;
                    }
                }
                return false;
            },
            $_checkThemeLayers() {
                // Check if loaded layers contain layers of a certain type
                // if not, give a warning
                const themeLayers = this.getAllOrderedLayers.filter(
                    (l) => l.type === 'theme'
                );

                if (themeLayers.length === 0) {
                    this.$store.commit('ADD_STATUS_MESSAGE', {
                        message: this.$t('infoThemeNotAvailable'),
                        variant: 'warning',
                        autoHideDelay: 5000
                    });
                }
            }
        }
    };
</script>

<style scoped lang="scss">
.map-container {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transition: all 1s ease-in-out;
    overflow: hidden;
}

.baselayer-container {
    position: absolute;
    display: flex;
    @include z-index(map-controls);
    transition: all 350ms ease-in 0s;
    left: 10px;
    bottom: 17px;

    .scale-container {
        position: relative;
        display: flex;
        align-items: flex-end;

        .scaleline {
            position: relative;
            bottom: 16px;
            left: -6px;
        }

        .scaleSelector {
            font-size: 12px;
            height: 24px;
        }
    }
}

.footer-link-container {
    position: absolute;
    display: flex;
    bottom: 0.5px;
    line-height: 1;
    left: 10px;
    transition: all 350ms ease-in 0s;
}

.create-feedback {
    @include z-index(web-search);
    position: absolute;
    margin-top: 5px;
    top: 12px;
    width: 225px;
    left: 50%;
    transform: translate(-50%);

    .large-navbar & {
        top: var(--paneltop);
    }
}
/*-------------------------------------
max-width : 985px (when searchbar is reached)
---------------------------------------*/
@media (max-width: 775px) {
    .create-feedback.has-search {
        @include margin-top-navbar-search;
    }
}
/*-------------------------------------
max-width : 767px
---------------------------------------*/
@media (max-width: 767px) {
    .baselayer-container {
        left: 10px;
    }

    .footer-link-container {
        left: 10px;
    }
}
</style>
